import { MonetizationIcon, Skeleton, Text } from '@mercai/clever'
import { useTranslation } from 'react-i18next'


import { CardInfo } from '@/components/card-info'
import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency } from '@/helpers'

import type { CardSupplyOrderTotalValuesProps } from './card-supply-order-total-values.type'

import styles from './card-supply-order-total-values.module.css'

const CardSupplyOrderTotalValuesDefault = ({
  data,
}: CardSupplyOrderTotalValuesProps) => {
  const { t } = useTranslation('screenSupplyOrderDetail')

  const {
    currency,
    total,
    iva,
    subtotal,
    dollarExchangeRate,
    destinationBank,
  } = {
    iva: Number(data?.iva || 0),
    subtotal: Number(data?.totalWithoutIva || 0),
    total: Number(data?.total || 0),
    dollarExchangeRate: Number(data?.payments?.[0]?.dollarExchangeRate || 0),
    currency: data?.payments?.[0]?.currency || 'USD',
    destinationBank:
      data?.payments?.[0]?.statements?.[0]?.transaction?.destinationBank || '-',
  }

  return (
    <CardInfo
      icon={<MonetizationIcon />}
      title={t('cardSupplyOrderTotalValuesTitle')}
    >
      <div>
        <div className={styles['card-item']}>
          <div>
            <Text>
              {t('cardSupplyOrderTotalValuesDollarExchangeRateLabel')}
            </Text>
          </div>
          <div>
            <Text>
              <strong>
                1.00 USD ={' '}
                {formatCurrency(dollarExchangeRate || 0, 'es-MX', 'MXN', 5)}
              </strong>
            </Text>
          </div>
        </div>
        <div className={styles['card-item']}>
          <div>
            <Text>{t('cardSupplyOrderTotalValuesBankLabel')}</Text>
          </div>
          <div>
            <Text>
              <strong>{destinationBank || '-'}</strong>
            </Text>
          </div>
        </div>

        <div className={styles['total-container']}>
          <div className={styles['card-item']}>
            <Text>{t('cardSupplyOrderTotalValuesSubtotalLabel')}</Text>
            <Text>
              {formatCurrency(+subtotal, 'es-MX', currency || 'MXN', DECIMAL_PLACES_DEFAULT)}
            </Text>
          </div>

          <div className={styles['card-item']}>
            <Text>{t('cardSupplyOrderTotalValuesIVALabel')}</Text>
            <Text>{formatCurrency(+iva, 'es-MX', currency || 'MXN', DECIMAL_PLACES_DEFAULT)}</Text>
          </div>

          <div className={styles['total-card']}>
            <Text variant="title-large">TOTAL</Text>
            <Text variant="title-large">
              {formatCurrency(+total, 'es-MX', currency || 'MXN', DECIMAL_PLACES_DEFAULT)}
            </Text>
          </div>
        </div>
      </div>
    </CardInfo>
  )
}

const CardSupplyOrderTotalValuesLoading = () => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  return (
    <CardInfo
      icon={<MonetizationIcon />}
      title={t('orderPaymentShowSummaryValuestitle')}
    >
      <div>
        <div className={styles['card-item']}>
          <div>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>
          <div>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>
        </div>
        <div className={styles['card-item']}>
          <div>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>
          <div>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>
        </div>

        <div className={styles['total-container']}>
          <div className={styles['card-item']}>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>

          <div className={styles['card-item']}>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>

          <div className={styles['total-card']}>
            <Text variant="title-large">
              <Skeleton width="7rem" height="1rem" />
            </Text>
            <Text variant="title-large">
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>
        </div>
      </div>
    </CardInfo>
  )
}

export const CardSupplyOrderTotalValues = (
  props: CardSupplyOrderTotalValuesProps,
) => {
  if (props?.isLoading) {
    return <CardSupplyOrderTotalValuesLoading />
  }

  return <CardSupplyOrderTotalValuesDefault {...props} />
}
